import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import parse from 'html-react-parser';
import { _ } from '_locales';
import { cts_secure } from '_cts';
import apis from '_apis';
import { colors } from '_styles';
import { Drawer } from '_components';
import { eventTracker, isRTL } from '_helpers';

import Loader from './Loader';

/**
 * import styles
 */

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px;
  background-color: #fff;
  text-align: left !important;
`;

const ContentContainer = styled.div`
  width: 100%;
  padding: 30px;
  box-sizing: border-box;
  max-width: 500px;
  margin: 0 auto;
  text-align: left !important;
`;

const TitleContainer = styled.div`
  padding: 0;
  display: flex;
  width: 100%;
  text-align: left !important;
`;

const Details = styled.div`
  margin: 0;
  padding: 0;
  border: 0;
  direction: ${() => (isRTL() ? 'rtl' : 'ltr')};
  text-align: left !important;
   
  font-size: 16px !important;
  font-weight: 400 !important;
  line-height: 1.7em;

  ul {
    color: #666;
    font-size: 15px !important;
    text-align: left !important; 
    line-height: 1.6em;
    font-weight: 500 !important;
  }

  p {
    line-height: 1.7em;
    font-size: 15px !important;
    text-align: left !important; 
    font-weight: 500 !important;
    color: #666;
  }

  title {
    display: block !important;
    font-size: 16px !important;
    text-align: left !important; 
    align-self: center;
    font-weight: 400 !important;
    margin-bottom: 10px;
  }

  b {
    font-weight: bold !important;
    text-align: left !important;
  }

  a {
    color: ${colors.RC_ACTION} !important;
    text-decoration: none !important;
    text-align: left !important;
    &:hover {
      color: ${colors.RC_ACTION} !important;
      text-decoration: none !important;
    }
  }
`;

const Description = styled.div`
   padding-top: 20px;
   text-align: left !important;  
`;

const GeneralContent = styled.ul`
  border: 0;
  padding: 0;
  text-align: left !important;
  &::-moz-focus-inner {
    border: 0;
  }

  p {
    padding: 17px 0 !important;
    text-align: left !important;
  }

  li {
    list-style: none;
    text-align: left !important;
  }
`;

const DescriptionBox = styled.div`
  direction: ${() => (isRTL() ? 'rtl' : 'ltr')};
`;

const Text = styled.h1`
  display: inline-block !important;
  font-weight: 400 !important;
  font-size: 16px !important;
  text-align: left !important;
`;
// End styles

/**
 * This component is for preventing the collection requirements with their own details
 *
 * @author Alireza Zebardast
 * @param {object} plugin
 * @param {object} config
 */
const CollectionRequirements = ({
  plugin, config, props
}) => {
  const { projectId } = plugin;
  const {
    selectedLang, districtId, collection, localityId
  } = config;

  const { location } = props;

  /* Hooks */
  const [colContent, setColContent] = useState({});
  const [pageContent, setPageContent] = useState({});
  const [colReqIcon, setColReqIcon] = useState({});
  const [loading, setLoading] = useState(true);

  const {
    id: collectionId, month, day, curYear
  } = location.state;

  /**
   * Event Tracker
   */
  useEffect(() => {
    eventTracker('CMS - Collection Requirements Page', collectionId?.slice(11));
  }, [collectionId]);

  useEffect(() => {
    const getCollectionInformation = async (localityId, collectionId) => {
      try {
        setLoading(true);
        const { data, status } = await cts_secure('get', apis.getCollectionDetails(localityId, collectionId, selectedLang));

        if (
          !data.header.success
          && status !== 200
        ) throw new Error('Could not get collection details data.');

        const { collectionDetails, pageDetails } = data.content.response;

        /* for collection in collection requirements */
        if (collectionDetails) {
          const {
            time,
            symbol_shape,
            links,
            coll_name,
            coll_symbol_colour,
            coll_details,
            set_out_time_dscr
          } = collectionDetails;

          setColContent({
            time,
            shape: symbol_shape,
            links,
            name: coll_name,
            color: coll_symbol_colour,
            coll_details,
            set_out_time_dscr,
          });

          const colReqIconTemp = collectionDetails.links.map(() => ({ path: '', shapeName: '', iconicShape: '' }));
          for (let i = 0; i < collectionDetails.links.length; i += 1) {
            const { hrefPart } = collectionDetails.links[i];

            Object.keys(collection).map((index) => {
              if (hrefPart === collection[index].details.path) {
                colReqIconTemp[i] = {
                  path: hrefPart,
                  shapeName: collection[index].shapeName,
                  iconicShape: collection[index].iconicShape,
                };
              }

              return undefined;
            });
          }

          setColReqIcon(colReqIconTemp);
        }

        /* for page in collection requirements */
        if (pageDetails) {
          const {
            links,
            page_bar_title,
            page_content,
          } = pageDetails;

          setPageContent({
            links,
            name: page_bar_title,
            coll_details: page_content,
          });
        }

        return undefined;
      } catch (e) {
        setColContent({});
        return Promise.reject(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (location.state) getCollectionInformation(localityId, collectionId);
  }, [collection, districtId, collectionId, selectedLang,
    location.state, projectId, localityId]);
  return (
    <>
      {
        loading
          ? (<Loader />)
          : (
            <>

              {/* set out time */}
              <Container>
                {/* <CTAofCMS /> */}
                <Text style={{
                  marginLeft: '0', fontWeight: '600', color: '#33817d', margin: '0 15px'
                }}
                >
                  {_('collectionRequirements.set_out_time')}
                </Text>
                <Text style={{
                  marginLeft: '0', fontWeight: '600', color: '#33817d', margin: '0 15px'
                }}
                >
                  {
                    Object.keys(colContent).length
                      && colContent.time
                      ? colContent.time
                      : null
                  }
                </Text>
              </Container>

              <ContentContainer>
                {/* header title */}
                <TitleContainer>
                  {
                    Object.keys(colContent).length
                      && colContent.name
                      ? (
                        <Text style={{ color: '#000', margin: '0' }}>{colContent.name}</Text>
                      ) : null
                  }
                  {
                    Object.keys(pageContent).length
                      && pageContent.name
                      ? (
                        <Text style={{ color: '#000', margin: '0' }}>{pageContent.name}</Text>
                      ) : null
                  }
                </TitleContainer>

                {/* content */}
                {
                  Object.keys(colContent).length
                    && colContent.coll_details
                    ? (
                      <Details>
                        {parse(colContent.coll_details)}
                      </Details>
                    ) : null
                }
                {
                  Object.keys(pageContent).length
                    && pageContent.coll_details
                    ? (
                      <Details>
                        {parse(pageContent.coll_details)}
                      </Details>
                    ) : null
                }
                {
                  Object.keys(colContent).length
                    && colContent.set_out_time_dscr
                    ? (
                      <DescriptionBox>
                        <Description>
                          <Text style={{ color: '#000', margin: '0' }}>{_('collectionRequirements.set_out_description')}</Text>
                        </Description>
                        <Details>{parse(colContent.set_out_time_dscr)}</Details>
                      </DescriptionBox>
                    ) : null
                }
                {
                  Object.keys(colContent).length
                    && colContent.links
                    ? (
                      <GeneralContent tabIndex="-1">
                        {
                          colContent.links.map((lnk) => (
                            <li>
                              <Drawer
                                link={lnk}
                                lang={selectedLang}
                                name={colContent.name}
                                key={lnk.drawer_title}
                                colReqIcon={colReqIcon}
                              />
                            </li>
                          ))
                        }
                      </GeneralContent>
                    ) : null
                }
                {
                  Object.keys(pageContent).length
                    && pageContent.links
                    ? (
                      <GeneralContent tabIndex="-1">
                        {
                          pageContent.links.map((lnk) => (
                            <li>
                              <Drawer
                                link={lnk}
                                name={pageContent.name}
                                key={lnk.drawer_title}
                              />
                            </li>
                          ))
                        }
                      </GeneralContent>
                    ) : null
                }
              </ContentContainer>
            </>
          )
      }
    </>
  );
};

export default CollectionRequirements;
